import type { ClickInput, PageViewInput } from '@/api/types';
import { env } from '@/config/env';

/**
 * Counts page views and clicks on links/buttons for the console's "Visitas".
 *
 * Nothing typed in forms is ever read. People who ask not to be tracked (Do Not
 * Track / Global Privacy Control) are not counted. The session id lives only
 * in this tab (sessionStorage) and there are no cookies.
 */

interface RouterLike {
  state: { location: { pathname: string; search: string } };
  subscribe: (listener: (state: RouterLike['state']) => void) => () => void;
}

interface Current {
  id: string;
  startedAt: number;
  ended: boolean;
}

const SESSION_KEY = 'app.visit-session';
const CLICKABLE = 'a[href], button, [role="button"], [role="tab"], [role="menuitem"], [data-track]';

export function trackingAllowed(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return !(nav.globalPrivacyControl === true || nav.doNotTrack === '1');
}

function uuid(): string {
  return crypto.randomUUID();
}

function sessionId(): string | null {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = uuid();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function send(path: string, body: unknown, beacon = false) {
  const url = `${env.apiUrl}${path}`;
  const payload = JSON.stringify(body);
  try {
    if (beacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      return;
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => undefined);
  } catch {
    // Statistics never get in the way of the page.
  }
}

/** Short visible text of a clicked element (aria-label first). */
export function labelOf(element: Element): string | null {
  const text =
    element.getAttribute('data-track') ||
    element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    (element.textContent ?? '');
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean ? clean.slice(0, 120) : null;
}

export function startTracking(router: RouterLike, isAuthenticated: () => boolean): () => void {
  if (!trackingAllowed()) return () => undefined;
  let current: Current | null = null;
  let lastKey = '';
  let firstView = true;

  const end = (beacon: boolean) => {
    if (!current || current.ended) return;
    current.ended = true;
    send(
      `/analytics/pageview/${current.id}/end`,
      { durationMs: Math.min(Date.now() - current.startedAt, 24 * 3600 * 1000) },
      beacon,
    );
  };

  const view = (location: { pathname: string; search: string }) => {
    const key = location.pathname + location.search;
    if (key === lastKey) return;
    lastKey = key;
    end(false);
    current = { id: uuid(), startedAt: Date.now(), ended: false };
    const body: PageViewInput = {
      id: current.id,
      url: key,
      referrer: firstView ? document.referrer || null : null,
      sessionId: sessionId(),
      language: navigator.language || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      screenWidth: window.screen?.width ?? null,
      screenHeight: window.screen?.height ?? null,
      authenticated: isAuthenticated(),
    };
    firstView = false;
    send('/analytics/pageview', body);
  };

  const onClick = (event: MouseEvent) => {
    if (!current || !(event.target instanceof Element)) return;
    const element = event.target.closest(CLICKABLE);
    if (!element || element.closest('[data-no-track]')) return;
    const link = element instanceof HTMLAnchorElement ? element : null;
    const width = document.documentElement.clientWidth || 1;
    const body: ClickInput = {
      viewId: current.id,
      sessionId: sessionId(),
      url: window.location.pathname + window.location.search,
      kind: link ? 'link' : element.matches('button, [role="button"]') ? 'button' : 'other',
      label: labelOf(element),
      target: link ? link.href : null,
      xPct: Math.max(0, Math.min(100, Math.round((event.clientX / width) * 100))),
      yPx: Math.max(0, Math.round(event.clientY + window.scrollY)),
    };
    // A link may unload the page right away: the beacon survives that.
    send('/analytics/click', body, Boolean(link));
  };

  const onHide = () => {
    if (document.visibilityState === 'hidden') end(true);
  };
  const onLeave = () => end(true);

  view(router.state.location);
  const unsubscribe = router.subscribe((state) => view(state.location));
  document.addEventListener('click', onClick, { capture: true });
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onLeave);
  return () => {
    unsubscribe();
    document.removeEventListener('click', onClick, { capture: true });
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', onLeave);
  };
}
